import { useState } from "react";
import axios from "axios";
import { Dice5, X, Check, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LogoSuggestionsPicker — Modal mit 6 generierten SVG-Logo-Vorschlägen
 * (Initialen-basiert, in der aktuellen Theme-Akzentfarbe). Nützlich, wenn
 * der Kunde noch kein eigenes Logo hat — Vertriebler kann eine Variante
 * für die Demo wählen.
 *
 * Props:
 *   open: bool
 *   projectId: string
 *   onSelect: (dataUrl) => void  // setzt logo_url
 *   onClose: () => void
 */
export const LogoSuggestionsPicker = ({ open, projectId, onSelect, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [logos, setLogos] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/generate-logo-suggestions`,
        null,
        { withCredentials: true }
      );
      setLogos(r.data?.logos || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Logo-Vorschläge konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  // Lazy-load on open
  if (open && logos.length === 0 && !loading) {
    fetchSuggestions();
  }

  if (!open) return null;

  const apply = () => {
    const chosen = logos.find(l => l.id === selectedId);
    if (!chosen) return;
    onSelect?.(chosen.svg_data_url);
    onClose?.();
    toast.success("Logo-Vorschlag übernommen — speichern nicht vergessen");
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="logo-suggestions-modal"
    >
      <div
        className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Dice5 size={14} /> Logo-Vorschläge
            </h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Generiert aus Firmen-Initialen + Akzentfarbe — perfekt als Demo-Platzhalter
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            data-testid="logo-modal-close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="py-12 flex flex-col items-center gap-3 text-muted-foreground">
              <Loader2 size={24} className="animate-spin" />
              <span className="text-xs">Logos werden generiert…</span>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3" data-testid="logo-suggestions-grid">
                {logos.map(l => {
                  const active = selectedId === l.id;
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => setSelectedId(l.id)}
                      className={`group relative bg-muted/30 rounded-lg border-2 transition-all p-3 hover:scale-[1.02] ${
                        active ? "border-primary ring-2 ring-primary/30" : "border-border hover:border-foreground/40"
                      }`}
                      data-testid={`logo-suggestion-${l.id}`}
                    >
                      <div className="aspect-square bg-card rounded-md flex items-center justify-center overflow-hidden mb-2">
                        <img src={l.svg_data_url} alt={l.label} className="w-full h-full object-contain" />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold">{l.label}</span>
                        {active && <Check size={12} className="text-primary" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={fetchSuggestions}
                  disabled={loading}
                  className="text-xs gap-1.5 h-8"
                  data-testid="logo-regenerate-btn"
                >
                  <Dice5 size={11} /> Neu würfeln
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onClose}
                    className="text-xs h-8"
                    data-testid="logo-modal-cancel"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={apply}
                    disabled={!selectedId}
                    className="text-xs h-8 gap-1.5"
                    data-testid="logo-modal-apply"
                  >
                    <Check size={11} /> Übernehmen
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
